import { useMutation, useQuery } from 'react-query'
import { PatientServices } from '../../services/PatientServices/PatientServices'
import {
  AttendancePayload,
  ManualBillingTypes,
  PayloadReschedule,
  PaymentReverseProps,
  RegisterPatientData,
  RemoveAttachment,
  SendAttachment,
  SmartSearchProfessional,
  UnlinkProfessional,
  UploadPicturePatientProps,
} from '../../services/PatientServices/PatientServices.types'
import { toast } from '@conexasaude/conexa-hero'
import { useModalStore } from '@/store/useModal'
import { DataResults } from '../../pages/Patient/components/LinkedPsychologists/LinkedPsychologists.types'
import { PsychologistSearchListParams } from '../../services/mapper/PatientsMapper/PsychologistSearchListMapper/PsychologistSearchListMapper.types'
import usePatientStore from '../../store/Patient/PatientStore'
import { IQueryParams } from '@/types'
import {
  AnonymizeLgpdPayload,
  BlockOrUnblockPatientPayload,
  ResetPasswordPayload,
  SendAccessLinkPayload,
  UnlockLoginAttemptsPayload,
} from '../../pages/Patient/Patient.types'
import { useNavigate } from 'react-router-dom'
import {
  EligibilityRequestBody,
  EligibilityWellhubParams,
  FormRegisterPatient,
} from '../../pages/Patient/components/RegisterPatient/RegisterPatient.types'
import { STALE_TIMES } from '@/constants'
import useEligibilityCheckModal from '../../pages/Patient/components/RegisterPatient/components/Modal/EligibilityCheck/EligibilityCheck.hook'
import useClinicConfigStore from '../../store/ClinicConfig/ClinicConfig'
import useEligibilityWellhubCheckModal from '../../pages/Patient/components/RegisterPatient/components/Modal/EligibilityCheckWellhub/EligibilityCheckWellhub.hook'
import { transformClinicConfig } from '@/utils/transformClinicConfig'
import { ShareService } from '@/queries/services'

export const useGetAttendanceList = () => {
  const { data, mutateAsync, isLoading } = useMutation(
    (params: AttendancePayload) => PatientServices.getAttendanceList(params),
  )

  return {
    attendanceData: data,
    attendanceAppointmentIsLoading: isLoading,
    attendanceAppointmentMutation: mutateAsync,
  }
}

export const usePostPaymentReverse = () => {
  const closeModal = useModalStore(state => state.closeModal)
  const { mutateAsync, isLoading } = useMutation(
    (params: PaymentReverseProps) => PatientServices.getPaymentReverse(params),
    {
      onSuccess: async () => {
        toast({
          message: 'Pagamento estornado.',
          variant: 'success',
        })
        closeModal()
      },
    },
  )

  return {
    paymentReverseIsLoading: isLoading,
    paymentReverseMutation: mutateAsync,
  }
}

export const usePostManualBillingWithOutcome = () => {
  const closeModal = useModalStore(state => state.closeModal)
  const { mutateAsync, isLoading } = useMutation(
    ({
      idAttendance,
      outcomeType,
      params,
    }: {
      idAttendance: number
      outcomeType: string
      params: ManualBillingTypes
    }) =>
      PatientServices.getManualBillingWithOutcome(
        idAttendance,
        outcomeType,
        params,
      ),
    {
      onSuccess: async outcomeType => {
        if (outcomeType === 'com-cobranca') {
          toast({
            message: 'Desfecho adicionado.',
            variant: 'success',
          })
        } else {
          toast({
            message: 'Desfecho adicionado e cobrança realizada.',
            variant: 'success',
          })
        }
        closeModal()
      },
    },
  )

  return {
    manualBillingWithOutcomeIsLoading: isLoading,
    manualBillingWithOutcomeMutation: mutateAsync,
  }
}

export const usePostManualBillingBillOrReserve = () => {
  const closeModal = useModalStore(state => state.closeModal)
  const { mutateAsync, isLoading } = useMutation(
    ({
      idAttendance,
      outcomeType,
      params,
    }: {
      idAttendance: number
      outcomeType: string
      params: ManualBillingTypes
    }) =>
      PatientServices.getManualBillingBillOrReserve(
        idAttendance,
        outcomeType,
        params,
      ),
    {
      onSuccess: async outcomeType => {
        if (outcomeType === 'cobrar') {
          toast({
            message: 'Cobrança realizada.',
            variant: 'success',
          })
        } else {
          toast({
            message: 'Valor reservado.',
            variant: 'success',
          })
        }
        closeModal()
      },
    },
  )

  return {
    manualBillingBillOrReserveIsLoading: isLoading,
    manualBillingBillOrReserveMutation: mutateAsync,
  }
}

export const useGetServiceDetailsList = (id: string) => {
  const { data } = useQuery(['storedServicesDetailsList'], () =>
    PatientServices.getServicesDetailList(id),
  )

  return {
    serviceDetailsList: data,
  }
}

export const useGetPatientDatailsData = (id: string) => {
  const { updateValue } = usePatientStore()

  const { data, refetch } = useQuery(
    ['storedPatientData'],
    () => PatientServices.getPatientDatailsData(id),
    {
      onSuccess: data => {
        updateValue('hasScreening', data?.screening === 'Sim')
      },
    },
  )

  return {
    patientData: data,
    patientDataRefetch: refetch,
  }
}

export const useGetHistoryData = () => {
  const { updateValue } = usePatientStore()
  const { mutateAsync, isLoading } = useMutation(
    (params?: DataResults) => PatientServices.getHistoryData(params),
    {
      onSuccess: data => {
        updateValue('historyData', data)
      },
    },
  )

  return {
    historyIsLoading: isLoading,
    historyMutation: mutateAsync,
  }
}

export const usePostUnlinkProfessional = () => {
  const closeModal = useModalStore(state => state.closeModal)
  const { historyMutation } = useGetHistoryData()

  const { mutateAsync, isLoading } = useMutation(
    (payload: UnlinkProfessional) =>
      PatientServices.postUnlinkProfessional(payload),
    {
      onSuccess: (_, query) => {
        historyMutation({
          patientId: Number(query?.patientId),
          itemsPerPage: 10,
          page: 1,
        })
        toast({
          message: 'Profissional desvinculado!',
          variant: 'success',
        })
        closeModal()
      },
    },
  )

  return {
    unlinkProfessionalMutation: mutateAsync,
    unlinkProfessionalIsLoading: isLoading,
  }
}

export const useGetPatientDetailsList = (id: number) => {
  const { data } = useQuery(
    ['patientDetailsList'],
    () => PatientServices.getPatientDetailsList(id),
    {
      enabled: Boolean(id),
    },
  )

  return {
    patientDetailsList: data,
  }
}

export const useGetCallDetails = (id: string) => {
  const { data } = useQuery(['storedCallDetails'], () =>
    PatientServices.getCallDetails(id),
  )

  return {
    callDetailsList: data,
  }
}

export const useGetUserPatient = (id: string) => {
  const { data } = useQuery(['storedGetUserPatient'], () =>
    PatientServices.getUsers(id),
  )

  return {
    getUserPatient: data,
  }
}

export const useGetUserProfessional = (id: string) => {
  const { data } = useQuery(['storedGetUserProfessional'], () =>
    PatientServices.getUsers(id),
  )

  return {
    getUserProfessional: data,
  }
}

export const useGetOutcomeList = (id: number) => {
  const { data } = useQuery(['storedOutcomeList'], () =>
    PatientServices.getOutcomeList(id),
  )

  return {
    outcomeData: data,
  }
}

export const useGetReasonChargeback = () => {
  const { data } = useQuery(['storedReasonChargeback'], () =>
    PatientServices.reasonChargeback(),
  )

  return {
    reasonChargeback: data,
  }
}

export const useGetStatusAttendance = () => {
  const { data } = useQuery(['statusAttendance'], () =>
    PatientServices.getStatusAttendance(),
  )
  return {
    statusAttendance: data,
  }
}

export const useGetProfessional = (patientId: number) => {
  const { data } = useQuery(
    ['getProfessional', patientId],
    () => PatientServices.getProfessionalByPatientName(patientId),
    {
      enabled: Boolean(patientId),
    },
  )
  return {
    professionalList: data,
  }
}

export const useGetCollectionAttemptsOrOutcome = (idService: string) => {
  const { data } = useQuery(['getTentativasCobrancas', idService], () =>
    PatientServices.getCollectionAttemptsOrOutcome(idService),
  )
  return {
    collectionAttemptsList: data,
  }
}

export const useGetCreditCard = (idService: number) => {
  const { data } = useQuery(['getCreditCard', idService], () =>
    PatientServices.getCreditCard(idService),
  )
  return {
    creditCardList: data,
  }
}

export const useGetAllOutcome = () => {
  const { data } = useQuery(['getAllOutcome'], () =>
    PatientServices.getAllOutcome(),
  )
  return {
    allOutcomeList: data,
  }
}

export const useGetPaymentSituation = () => {
  const { data } = useQuery(['paymentSituation'], () =>
    PatientServices.getPaymentSituation(),
  )
  return {
    situationPayment: data,
  }
}

export const useGetGenerateNote = () => {
  const { data, mutateAsync, isLoading } = useMutation(
    (id: number) => PatientServices.getGenerateNote(id),
    {
      onSuccess: async () => {
        toast({
          message: 'A Nota Fiscal será disponibilizada em até 10 minutos.',
          variant: 'success',
        })
      },
    },
  )

  return {
    generateNoteData: data,
    generateNoteIsLoading: isLoading,
    generateNoteMutation: mutateAsync,
  }
}

export const useSendImmediateAttachment = () => {
  const mutation = useMutation(
    (payload: SendAttachment) =>
      PatientServices.sendImmediateAttachment(payload),
    {
      onSuccess: () => {
        toast('Arquivo anexado com sucesso!')
      },
    },
  )

  return {
    sendAttachmentIsLoading: mutation.isLoading,
    sendAttachmentMutation: mutation.mutateAsync,
  }
}

export const useRemoveImmediateAttachment = () => {
  const mutation = useMutation(
    (data: RemoveAttachment) => PatientServices.removeImmediateAttachment(data),
    {
      onSuccess: () => {
        toast('Arquivo removido com sucesso!')
      },
    },
  )

  return {
    removeAttachmentIsLoading: mutation.isLoading,
    removeAttachmentMutation: mutation.mutateAsync,
  }
}

export const useGetSearchAttachment = () => {
  const { data, mutateAsync, isLoading } = useMutation((id: number) =>
    PatientServices.searchImmediateAttachment(id),
  )

  return {
    attachmentList: data,
    attachmentListIsLoading: isLoading,
    attachmentListMutate: mutateAsync,
  }
}

export const useRescheduleAppointmentQuery = () => {
  const { closeModal } = useModalStore()

  const mutation = useMutation(
    (payload: PayloadReschedule) =>
      PatientServices.rescheduleAppointment(payload),
    {
      onSuccess: () => {
        toast('Atendimento reagendado com sucesso!')

        closeModal()
      },
    },
  )

  return {
    rescheduleAppointmentMutation: mutation?.mutateAsync,
    rescheduleAppointmentIsLoading: mutation?.isLoading,
  }
}

export const useGetPsychologistSearchFilters = () => {
  const { data } = useQuery(['searchPsychologistFilters'], () =>
    PatientServices.getPsychologistSearchFilters(),
  )

  return {
    searchPsychologistFilters: data,
  }
}

export const useGetPsychologistSearchList = (
  queryParams: PsychologistSearchListParams,
) => {
  const { updateValue } = usePatientStore()

  const { data, isLoading, isSuccess, refetch } = useQuery(
    ['searchPsychologistList', queryParams],
    () => PatientServices.getPsychologistSearchList(queryParams),
    {
      enabled: false,
      retry: false,
      onSuccess: data => {
        updateValue('psychologistListData', data)
        updateValue('psychologistListFilter', queryParams)
      },
      onError: () => {
        updateValue('psychologistListData', null)
      },
    },
  )

  return {
    psychologistList: data,
    psychologistListIsLoading: isLoading,
    psychologistListIsSuccess: isSuccess,
    psychologistListRefetch: refetch,
  }
}

export const useLinkPsychologist = (patientId: string) => {
  const { patientDataRefetch } = useGetPatientDatailsData(patientId)
  const { historyMutation } = useGetHistoryData()

  const { isSuccess, mutateAsync } = useMutation(
    (params: Record<string, number>) =>
      PatientServices.linkPsychologist(params),
    {
      onSuccess: () => {
        toast('Profissional vinculado!')
        patientDataRefetch()
        historyMutation({
          patientId: Number(patientId),
          itemsPerPage: 10,
          page: 1,
        })
      },
    },
  )

  return {
    LinkPsychologistIsSuccess: isSuccess,
    LinkPsychologistsMutation: mutateAsync,
  }
}

export const useGetPatientList = (onClose?: () => void) => {
  const { updateValue } = usePatientStore()

  const { isLoading, mutateAsync } = useMutation(
    (queryParams: IQueryParams) => PatientServices.getPatientList(queryParams),
    {
      onSuccess: data => {
        updateValue('patientData', data)

        if (onClose) {
          updateValue('currentPage', 1)
          onClose()
        }
      },
    },
  )

  return {
    patientListIsLoading: isLoading,
    patientListMutation: mutateAsync,
  }
}

export const useGetClinicList = (clinicName: string) => {
  const { data } = useQuery(
    [`clinicList`, clinicName],
    () => PatientServices.getClinicList(clinicName),
    {
      enabled: Boolean(clinicName),
    },
  )

  return {
    clinicList: data,
  }
}

export const useGetClinicPasswordCommunicationMedia = (clinicId: string) => {
  const { data } = useQuery(
    ['communicationMediaList'],
    () => PatientServices.getClinicPasswordCommunicationMedia(clinicId),
    { enabled: Boolean(clinicId) },
  )

  return {
    passwordCommunicationList: data,
  }
}

export const useSendResetPassword = (onClose: () => void) => {
  const { isLoading, mutateAsync } = useMutation(
    (payload: ResetPasswordPayload) =>
      PatientServices.sendResetPassword(payload),
    {
      onSuccess: () => {
        toast({ message: 'Senha enviada.', variant: 'success' })
        onClose()
      },
    },
  )

  return {
    sendResetPasswordIsLoading: isLoading,
    sendResetPasswordMutation: mutateAsync,
  }
}

export const useGetClinicCommunicationMedia = (clinicId: string) => {
  const { data } = useQuery(
    ['communicationMediaList'],
    () => PatientServices.getClinicCommunicationMedia(clinicId),
    { enabled: Boolean(clinicId) },
  )

  return {
    communicationList: data,
  }
}

export const useSendAccessLink = (onClose: () => void) => {
  const { isLoading, mutateAsync } = useMutation(
    (payload: SendAccessLinkPayload) => PatientServices.sendAccessLink(payload),
    {
      onSuccess: () => {
        toast({ message: 'Link de acesso enviado.', variant: 'success' })
        onClose()
      },
    },
  )

  return {
    sendAccessLinkIsLoading: isLoading,
    sendAccessLinkMutation: mutateAsync,
  }
}

export const useGetOwnerships = () => {
  const { data } = useQuery([`ownershipList`], () =>
    PatientServices.getOwnerships(),
  )

  return {
    ownershipList: data,
  }
}

export const useGetStatus = () => {
  const { data } = useQuery([`statusList`], () => PatientServices.getStatus())

  return {
    statusList: data,
  }
}

export const useGetSituations = () => {
  const { data } = useQuery([`situationList`], () =>
    PatientServices.getSituations(),
  )

  return {
    situationList: data,
  }
}

export const useGetSettingClinicById = () => {
  const { updateValue } = usePatientStore()
  const { data, mutateAsync } = useMutation(
    (clinicId: string | number) =>
      PatientServices.getSettingClinicById(clinicId),
    {
      onSuccess: data => {
        updateValue('flagClinicHasPatientPlan', data?.flagClinicHasPatientPlan)
      },
    },
  )

  return {
    canUnblockCreditCard: data?.canUnblockCreditCard,
    settingClinicMutation: mutateAsync,
  }
}

export const useUnblockCreditCard = () => {
  const { mutateAsync } = useMutation(
    (patientId: string) => PatientServices.unblockCreditCard(patientId),
    {
      onSuccess: () => {
        toast({
          message: 'Cartão de crédito desbloqueado com sucesso.',
          variant: 'information',
        })
      },
    },
  )

  return { unblockCreditCardMutation: mutateAsync }
}

export const useUnlockLoginAttempts = () => {
  const { mutateAsync } = useMutation(
    (payload: UnlockLoginAttemptsPayload) =>
      PatientServices.unlockLoginAttempts(payload),
    {
      onSuccess: () => {
        toast({
          message: 'Tentativa de login desbloqueada.',
          variant: 'information',
        })
      },
    },
  )

  return {
    unlockLoginAttemptsMutation: mutateAsync,
  }
}

export const useInactivatePatient = () => {
  const { filter } = usePatientStore()
  const { patientListMutation } = useGetPatientList()

  const { mutateAsync } = useMutation(
    (patientId: string) => PatientServices.inactivatePatient(patientId),
    {
      onSuccess: () => {
        toast({ message: 'Paciente inativado.', variant: 'information' })
        patientListMutation(filter)
      },
    },
  )

  return {
    inactivatePatientMutation: mutateAsync,
  }
}

export const useActivatePatient = () => {
  const { filter } = usePatientStore()
  const { patientListMutation } = useGetPatientList()

  const { mutateAsync } = useMutation(
    (patientId: string) => PatientServices.activatePatient(patientId),
    {
      onSuccess: () => {
        toast({ message: 'Paciente ativado.', variant: 'information' })
        patientListMutation(filter)
      },
    },
  )

  return {
    activatePatientMutation: mutateAsync,
  }
}

export const useBlockOrUnblockPatient = () => {
  const { selectedPatient, filter } = usePatientStore()
  const { patientListMutation } = useGetPatientList()

  const { mutateAsync } = useMutation(
    (payload: BlockOrUnblockPatientPayload) =>
      PatientServices.blockOrUnblockPatient(payload),
    {
      onSuccess: () => {
        const message =
          selectedPatient?.situation === 'Bloqueado'
            ? 'Paciente desbloqueado.'
            : 'Paciente bloqueado.'

        toast({ message, variant: 'information' })
        patientListMutation(filter)
      },
    },
  )

  return {
    blockOrUnblockPatientMutation: mutateAsync,
  }
}

export const useAnonymizeLgpd = () => {
  const { filter } = usePatientStore()
  const { closeModal } = useModalStore()
  const { patientListMutation } = useGetPatientList()

  const { isLoading, mutateAsync } = useMutation(
    (payload: AnonymizeLgpdPayload) => PatientServices.anonymizeLgpd(payload),
    {
      onSuccess: () => {
        toast({
          message: 'Dados do paciente anonimizados com sucesso.',
          variant: 'success',
        })
        patientListMutation(filter)
        closeModal()
      },
      onError: () => {
        toast({
          message: 'Não foi possível anonimizar os dados do paciente.',
          variant: 'error',
        })
      },
    },
  )

  return {
    anonymizeLgpdIsLoading: isLoading,
    anonymizeLgpdMutation: mutateAsync,
  }
}

export const useGetPatientHistory = (patientId: string) => {
  const { data, isLoading } = useQuery(
    ['patientHistory'],
    () => PatientServices.getPatientHistory(patientId),
    {
      enabled: Boolean(patientId),
      staleTime: 0,
      cacheTime: 0,
    },
  )

  return {
    patientHistory: data,
    patientHistoryIsLoading: isLoading,
  }
}

export const useGetPatientDependents = (patientId: string) => {
  const { data, isLoading } = useQuery(
    ['patientDependents'],
    () => PatientServices.getPatientDependents(patientId),
    { enabled: Boolean(patientId), staleTime: 0, cacheTime: 0 },
  )

  return {
    patientDependents: data,
    patientDependentsIsLoading: isLoading,
  }
}

export const useCreatePatient = () => {
  const navigate = useNavigate()

  const { isLoading, mutateAsync } = useMutation(
    (payload: FormRegisterPatient) => PatientServices.create(payload),
    {
      onSuccess: () => {
        navigate('/usuario/pacientes')
        toast('Paciente adicionado.')
      },
    },
  )

  return {
    createPatientIsLoading: isLoading,
    createPatientMutation: mutateAsync,
  }
}

export const useEditPatient = () => {
  const { isLoading, mutateAsync } = useMutation(
    (payload: FormRegisterPatient & Partial<RegisterPatientData>) =>
      PatientServices.edit(payload),
    {
      onSuccess: () => {
        toast('Alterações salvas.')
      },
    },
  )

  return {
    editPatientIsLoading: isLoading,
    editPatientMutation: mutateAsync,
  }
}

export const useUploadPicturePatient = () => {
  const { isLoading, mutateAsync } = useMutation(
    (params: UploadPicturePatientProps) =>
      PatientServices.uploadPicturePatient(params),
  )

  return {
    uploadPicturePatientIsLoading: isLoading,
    uploadPicturePatientMutation: mutateAsync,
  }
}

export const useGetPatientData = (patientId: string) => {
  const { data, isLoading, refetch } = useQuery(
    ['storedPermissionsList'],
    () => PatientServices.getPatientData(patientId),
    {
      enabled: Boolean(patientId),
      staleTime: STALE_TIMES.ONE_MINUTE,
    },
  )

  return {
    patientDataRefetch: refetch,
    patientData: data,
    patientDataIsLoading: isLoading,
  }
}

export const useEligibilityCheck = () => {
  const { handleEligibilityCheckModal } = useEligibilityCheckModal()
  const { isLoading, mutateAsync, data } = useMutation(
    (payload: EligibilityRequestBody) =>
      PatientServices.checkEligibility(payload),
    {
      onSuccess: () => {
        handleEligibilityCheckModal()
      },
    },
  )
  return {
    checkEligibilityIsLoading: isLoading,
    checkEligibilityMutation: mutateAsync,
    eligibilityData: data,
  }
}

export const useEligibilityWellhubCheck = () => {
  const { handleEligibilityCheckModal } = useEligibilityWellhubCheckModal()
  const { isLoading, mutateAsync, data } = useMutation(
    (payload: EligibilityWellhubParams) =>
      PatientServices.checkEligibilityWellhub(payload),
    {
      onSuccess: () => {
        handleEligibilityCheckModal()
      },
    },
  )
  return {
    checkEligibilityIsLoading: isLoading,
    checkEligibilityMutation: mutateAsync,
    eligibilityData: data,
  }
}

export const useSmartSearchAnalysis = () => {
  const { isLoading, mutateAsync, data } = useMutation(
    (params: SmartSearchProfessional) =>
      PatientServices.smartSearchAnalysis(params),
    {
      onSuccess(data) {
        if (data?.errorMsg) {
          toast({
            message: data.errorMsg,
            variant: 'error',
          })
        }
      },
    },
  )
  return {
    generateAnalysisIsLoading: isLoading,
    generateAnalysisMutation: mutateAsync,
    analysisData: data,
  }
}

export const useMutationGetClinicConfig = () => {
  const { updateValue } = useClinicConfigStore()
  const { data, mutateAsync, isLoading } = useMutation(
    (idClinic: string | number) => ShareService.getClinicConfig(idClinic),
    {
      onSuccess: data => {
        const transformedData = transformClinicConfig(data)
        updateValue('clinic', transformedData)
      },
    },
  )

  return {
    clinic: data ? transformClinicConfig(data) : null,
    getClinicConfigMutation: mutateAsync,
    getClinicConfigIsLoading: isLoading,
  }
}
